import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MetaHead } from '../components/MetaHead';
import { COMPANY_INFO, COMMON_CONTACT_CALLOUT } from '../data/scrapedData';

export function TreatmentOverviewPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    const formData = new FormData(e.currentTarget);
    const payload = {
      name: formData.get('name'),
      phone: formData.get('phone'),
      email: formData.get('email'),
      message: formData.get('message'),
      website_url: formData.get('website_url') || '',
    };

    try {
      const res = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setFormSubmitted(true);
      } else {
        setFormError('Der opstod en fejl. Prøv venligst igen eller ring til os direkte.');
      }
    } catch {
      setFormError('Netværksfejl. Prøv venligst igen eller ring på telefon 75 17 20 80.');
    } finally {
      setSubmitting(false);
    }
  };
  const treatmentLinks = [
    {
      title: "Afgiftning & Alkohol",
      path: "/behandling/afgiftning",
      tag: "Stof & Alkohol",
      icon: "fa-solid fa-heart-pulse",
      desc: "Professionel og skånsom afgiftning samt alkoholbehandling tilpasset hele din livssituation."
    },
    {
      title: "Primærbehandling",
      path: "/behandling/primaerbehandling",
      tag: "Intensivt forløb",
      icon: "fa-solid fa-users",
      desc: "Ca. 6 måneders forløb med fokus på personlig forandring, kognitiv terapi og gruppemøder."
    },
    {
      title: "Dobbeltdiagnose",
      path: "/dobbeltdiagnose",
      tag: "Specialiseret",
      icon: "fa-solid fa-brain",
      desc: "Målrettet behandling for personer med både misbrug og samtidige psykiatriske problemstillinger."
    },
    {
      title: "Botilbud",
      path: "/botilbud",
      tag: "Trygge rammer",
      icon: "fa-solid fa-house-chimney",
      desc: "Midlertidigt botilbud i Sejstrup med helhedsorienteret støtte ved misbrug og psykisk sårbarhed."
    },
    {
      title: "Tilbagefaldsbehandling og -forebyggelse",
      path: "/behandling/tilbagefaldsbehandling-og-forebyggelse",
      tag: "Fastholdelse",
      icon: "fa-solid fa-shield-halved",
      desc: "Metodisk arbejde med personlige advarselstegn, triggere og udarbejdelse af en konkret bedringsplan."
    },
    {
      title: "Halvvejshus og udslusning",
      path: "/behandling/eftervaern-og-udslusning",
      tag: "Overgang til hverdag",
      icon: "fa-solid fa-house-user",
      desc: "Gradueret overgang til egen bolig via Midtgaardens Halvvejshus i Esbjerg."
    },
    {
      title: "Dagbehandling",
      path: "/behandling/dagbehandling",
      tag: "Fleksibel struktur",
      icon: "fa-solid fa-sun",
      desc: "Struktureret dagligt forløb med gruppeterapi, DAT, træning og døgnberedskab."
    },
    {
      title: "Bio-psyko-sociale model",
      path: "/behandling/bio-psyko-sociale-model",
      tag: "Faglig tilgang",
      icon: "fa-solid fa-diagram-project",
      desc: "Helhedsorienteret forståelsesramme for de biologiske, psykologiske og sociale aspekter."
    },
    {
      title: "Behandling hos Midtgaarden (Borger)",
      path: "/behandling/borger",
      tag: "Information",
      icon: "fa-solid fa-hand-holding-heart",
      desc: "Information til privatpersoner og pårørende om forløb, aktiviteter, tryghed og mestring."
    }
  ];

  const criteriaList = [
    {
      title: "Afstand til vante miljøer",
      text: "Der er brug for afstand til miljøer og situationer, som gør det svært at ændre forbruget",
      icon: "fa-solid fa-compass"
    },
    {
      title: "Tidligere tilbagefald",
      text: "Der har været tilbagefald og behov for en mere intensiv behandlingsramme",
      icon: "fa-solid fa-shield-halved"
    },
    {
      title: "Samtidige problemstillinger",
      text: "Misbruget optræder sammen med psykiske, sociale eller adfærdsmæssige problemstillinger",
      icon: "fa-solid fa-brain"
    },
    {
      title: "Struktureret overgang",
      text: "Overgangen til hverdagen kræver planlagt efterværn eller udslusning",
      icon: "fa-solid fa-door-open"
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Døgnbehandling for misbrug, sådan får du ro | Midtgaarden",
    "description": "Få døgnbehandling for stofmisbrug og alkoholmisbrug i trygge rammer. Midtgaarden tilpasser forløbet til dig og støtter vejen mod en stabil hverdag.",
    "publisher": {
      "@type": "Organization",
      "name": "Den Selvejende Institution Midtgaarden",
      "url": "https://www.midtgaarden.dk"
    }
  };

  return (
    <>
      <MetaHead
        title="Døgnbehandling for misbrug | Midtgaarden - mere end 20 års erfaring"
        description="Få døgnbehandling for stofmisbrug og alkoholmisbrug i trygge rammer. Midtgaarden tilpasser forløbet til dig og støtter vejen mod en stabil hverdag."
        ogImage="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/gaardhave-med-havebassin-og-liggestole.jpg"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section className="relative bg-[#1C1C1E] text-white py-16 sm:py-24 overflow-hidden">
        {/* Background image overlay with soft gradient */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/gaardhave-med-havebassin-og-liggestole.jpg"
            alt="Brolagt gårdhave med anlagt havebassin og liggestole foran hvid bygning"
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1E] via-[#1C1C1E]/90 to-[#1C1C1E]/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 [text-shadow:_0_2px_12px_rgba(0,0,0,0.85)]">
          <div className="max-w-3xl space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#3885B8] text-white">
                <i className="fa-solid fa-house-chimney-medical text-[11px]" aria-hidden="true" />
                Døgnbehandling
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white/90 backdrop-blur-xs">
                For voksne fra 18 år
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Døgnbehandling for misbrug med ro til at arbejde med forandring
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-white/90 leading-relaxed font-normal">
              Når et stofmisbrug eller alkoholmisbrug fylder meget i hverdagen, kan det være svært at skabe forandring i de samme rammer. Hos Midtgaarden bor du på behandlingsstedet, mens du følger et individuelt forløb. Vores døgnbehandling er for voksne fra 18 år.
            </p>

            {/* CTA action buttons */}
            <div className="pt-2 flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href={COMPANY_INFO.phoneTel}
                className="h-14 px-8 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] active:bg-[#1F557B] text-white font-bold text-base shadow-lg transition-all duration-200 inline-flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8]"
              >
                <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                <span className="whitespace-nowrap">Ring: {COMPANY_INFO.phone}</span>
              </a>

              <a
                href={COMPANY_INFO.emailMailto}
                aria-label="Send e-mail til Midtgaarden"
                className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white shrink-0"
              >
                <i className="fa-solid fa-envelope text-lg" aria-hidden="true" />
              </a>

              <a
                href="#behandlinger"
                className="inline-flex items-center justify-center gap-2 h-14 px-6 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm sm:text-base font-semibold transition-colors backdrop-blur-xs border border-white/20"
              >
                <span>Se behandlingsformer</span>
                <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
              </a>
            </div>

            {/* Trust highlights */}
            <div className="pt-6 border-t border-white/15 text-xs sm:text-sm text-white/80">
              <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-4 sm:gap-6">
                <div className="flex items-center gap-2 shrink-0">
                  <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                  <span>20+ års erfaring</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                  <span>Cenaps-modellen</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                  <span>Klienter fra hele landet</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <i className="fa-solid fa-check text-[#3885B8]" aria-hidden="true" />
                  <span>Trygge faste rammer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1: HVORNÅR KAN DØGNBEHANDLING VÆRE DET RETTE VALG? */}
      <section className="py-16 sm:py-20 bg-white border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
              OVERVEJELSER OG VISITATION
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Hvornår kan døgnbehandling være det rette valg?
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
              <p>
                Døgnbehandling kan være relevant, når der er behov for afstand til rusmidler, faste rammer og en sammenhængende indsats. Det gælder også, hvis tidligere behandling ikke har skabt tilstrækkelig stabilitet, hvis der er behov for et{' '}
                <Link
                  to="/botilbud"
                  className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                >
                  botilbud
                </Link>
                , eller hvis psykiske og sociale forhold gør situationen mere kompleks.
              </p>
              <p>
                Der findes ikke én form for misbrugsbehandling, som passer til alle. Døgnbehandling af misbrug giver mulighed for at arbejde koncentreret med forbruget og de forhold, der ligger bag. Behov, mål og livssituation har derfor betydning for, hvordan forløbet bliver tilrettelagt.
              </p>
            </div>
          </div>

          {/* Criteria Cards (List replacement with visual design cards) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            {criteriaList.map((item, idx) => (
              <div
                key={idx}
                className="bg-[#F8FAFC] border border-[#1C1C1E]/10 rounded-none p-6 hover:border-[#3885B8]/50 transition-all flex items-start gap-4 shadow-2xs"
              >
                <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center shrink-0 text-lg">
                  <i className={item.icon} aria-hidden="true" />
                </div>
                <div className="space-y-1">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#3885B8]">
                    Faktor {idx + 1}
                  </span>
                  <p className="text-base font-semibold text-[#1C1C1E] leading-snug">
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2: NUVÆRENDE OVERSIGT MED LINKS TIL BEHANDLINGER */}
      <section id="behandlinger" className="py-16 sm:py-24 bg-[#F8FAFC] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
              BEHANDLINGSTILBUD
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Oversigt over behandlinger hos Midtgaarden
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4" />
            <p className="text-base sm:text-lg text-[#1C1C1E]/75 leading-relaxed pt-1">
              Vi tilbyder en helhedsorienteret og differentieret indsats. Gå på opdagelse i vores specifikke behandlingsområder, metoder og botilbud nedenfor:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {treatmentLinks.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="group bg-white p-6 rounded-none border border-[#1C1C1E]/10 shadow-xs hover:border-[#3885B8] hover:shadow-md transition-all duration-200 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#3885B8] bg-[#3885B8]/10 px-2.5 py-1 rounded-none">
                      {item.tag}
                    </span>
                    <div className="w-8 h-8 rounded-none bg-[#F8FAFC] group-hover:bg-[#3885B8] text-[#1C1C1E]/60 group-hover:text-white flex items-center justify-center transition-colors">
                      <i className={`${item.icon} text-xs`} aria-hidden="true" />
                    </div>
                  </div>

                  <h3 className="text-base sm:text-lg font-bold text-[#1C1C1E] group-hover:text-[#3885B8] transition-colors mb-2 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#1C1C1E]/70 leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                <div className="mt-5 pt-3 border-t border-[#1C1C1E]/5 text-xs font-bold text-[#3885B8] group-hover:underline flex items-center justify-between">
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>

          <div className="bg-white border border-[#1C1C1E]/10 rounded-none p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-2xs">
            <div className="space-y-1 max-w-2xl">
              <h4 className="text-base sm:text-lg font-bold text-[#1C1C1E]">
                Brug for rådgivning om det rette forløb?
              </h4>
              <p className="text-sm text-[#1C1C1E]/75 leading-relaxed">
                Vores faglige personale og visitation står altid klar til at drøfte den konkrete situation med både borgere, pårørende og kommunale sagsbehandlere.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <a
                href={COMPANY_INFO.phoneTel}
                className="px-5 py-2.5 rounded-full bg-[#3885B8] text-white text-xs sm:text-sm font-bold hover:bg-[#2A6E9B] transition-colors whitespace-nowrap shadow-2xs"
              >
                Ring {COMPANY_INFO.phone}
              </a>
              <Link
                to="/priser"
                className="px-5 py-2.5 rounded-full border border-[#1C1C1E]/20 hover:border-[#1C1C1E] text-[#1C1C1E] text-xs sm:text-sm font-semibold transition-colors whitespace-nowrap"
              >
                Se priser & takster
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: SÅDAN FOREGÅR MISBRUGSBEHANDLING SOM DØGNBEHANDLING */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            
            <div className="lg:col-span-7 space-y-8">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                  STRUKTUR OG PROGRESSION
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  Sådan foregår misbrugsbehandling som døgnbehandling
                </h2>
                <div className="w-16 h-1 bg-[#3885B8] my-4" />
                <p className="text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
                  Et forløb bliver tilrettelagt ud fra en faglig vurdering af misbruget og din samlede situation. Vi ser blandt andet på behovet for{' '}
                  <Link
                    to="/behandling/afgiftning"
                    className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    afgiftning
                  </Link>
                  , tidligere behandlingsforløb, risikoen for tilbagefald og eventuelle psykiske problemstillinger. Forløbet tilpasses løbende til dine mål og behov.
                </p>
              </div>

              {/* Subheading 1: Et forløb tilpasset den enkelte */}
              <div className="bg-[#F8FAFC] border-l-4 border-[#3885B8] p-6 sm:p-7 rounded-none border-y border-r border-[#1C1C1E]/10 shadow-2xs space-y-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-none bg-[#3885B8] text-white font-bold text-xs flex items-center justify-center shrink-0">
                    1
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-[#1C1C1E]">
                    Et forløb tilpasset den enkelte
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed pl-11">
                  Midtgaarden tilbyder differentierede behandlingsforløb frem for én fast løsning. Afgiftning, primærbehandling, tilbagefaldsforebyggelse og efterværn kan indgå, når det er relevant. De enkelte dele sammensættes ud fra din situation og den plan, der er lagt for opholdet.
                </p>
              </div>

              {/* Subheading 2: Fra behandling til en mere stabil hverdag */}
              <div className="bg-[#F8FAFC] border-l-4 border-[#1C1C1E] p-6 sm:p-7 rounded-none border-y border-r border-[#1C1C1E]/10 shadow-2xs space-y-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-none bg-[#1C1C1E] text-white font-bold text-xs flex items-center justify-center shrink-0">
                    2
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-[#1C1C1E]">
                    Fra behandling til en mere stabil hverdag
                  </h3>
                </div>
                <p className="text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed pl-11">
                  Overgangen fra opholdet til hverdagen er en vigtig del af indsatsen. Derfor kan behandlingen omfatte efterværn og udslusning i samarbejde med kommune eller myndighed. Målet er at skabe sammenhæng mellem det, du arbejder med under opholdet, og den hverdag du skal videre til.
                </p>
              </div>
            </div>

            {/* Side visual card with photo of Midtgaarden */}
            <div className="lg:col-span-5 space-y-6">
              <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-xl bg-white">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/gaardsplads-med-havebassin-og-liggestole-solskin.jpg"
                  alt="Gårdsplads med anlagt havebassin og liggestole foran hvid bygning på en solskinsdag"
                  className="w-full h-64 sm:h-72 object-cover rounded-none"
                />
                <div className="p-4 bg-white rounded-none border-t border-[#1C1C1E]/10 space-y-3">
                  <h4 className="font-bold text-base text-[#1C1C1E] flex items-center gap-2">
                    <i className="fa-solid fa-circle-info text-[#3885B8]" aria-hidden="true" />
                    Trygge fysiske rammer i Sejstrup
                  </h4>
                  <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                    Under døgnopholdet bor man på Midtgaarden i rolige, naturskønne omgivelser med plads til fordybelse og samvær i strukturerede rammer med døgndækket personale.
                  </p>
                  <div className="pt-2 border-t border-[#1C1C1E]/10 flex items-center justify-between text-xs text-[#1C1C1E]/80">
                    <span>Døgnbemanding</span>
                    <span className="font-semibold text-[#3885B8]">24 timer i døgnet</span>
                  </div>
                </div>
              </div>

              {/* Callout quote box */}
              <div className="bg-[#3885B8] text-white rounded-none p-6 space-y-2 shadow-xs">
                <i className="fa-solid fa-quote-left text-2xl text-white/40 block" aria-hidden="true" />
                <p className="text-sm font-medium text-white italic leading-relaxed">
                  "Målet er at skabe sammenhæng mellem det, du arbejder med under opholdet, og den hverdag du skal videre til."
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 4 & 5: STOFMISBRUG / ALKOHOLMISBRUG & KOMORBIDITET / DOBBELTDIAGNOSE */}
      <section className="py-16 sm:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
              FAGLIG PROFIL & METODER
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
              Helhedsorienteret tilgang og specialiseret indsats
            </h2>
            <div className="w-16 h-1 bg-[#3885B8] my-4 mx-auto" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Card 1: Døgnbehandling ved stofmisbrug og alkoholmisbrug */}
            <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 shadow-xs flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-xl">
                  <i className="fa-solid fa-wine-glass-crack" aria-hidden="true" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E] tracking-tight">
                  Døgnbehandling ved stofmisbrug og alkoholmisbrug
                </h3>
                <div className="space-y-4 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
                  <p>
                    Vores døgnbehandling ved stofmisbrug henvender sig til voksne, som har behov for at bo i en struktureret ramme under behandlingen. Vi tilbyder også døgnbehandling ved alkoholmisbrug. Indsatsen tager udgangspunkt i både rusmidlet, forbrugets omfang og konsekvenserne i hverdagen.
                  </p>
                  <p>
                    Behandlingen tager afsæt i Cenaps som behandlingsmodel og i et helhedsorienteret syn på fysiske, psykiske og sociale forhold. Det betyder, at vi ser på mere end selve rusmidlet. Vi inddrager også de forhold, som påvirker trivsel, funktionsevne og mulighed for udvikling.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-[#1C1C1E]/10 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#F8FAFC] text-xs font-semibold rounded-none border border-[#1C1C1E]/10 text-[#1C1C1E]">
                  Psykiatrisk udredning
                </span>
                <span className="px-3 py-1 bg-[#F8FAFC] text-xs font-semibold rounded-none border border-[#1C1C1E]/10 text-[#1C1C1E]">
                  Helhedssyn
                </span>
              </div>
            </div>

            {/* Card 2: Når misbrug og psykiske udfordringer hænger sammen */}
            <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-10 shadow-xs flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-xl">
                  <i className="fa-solid fa-brain" aria-hidden="true" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1C1C1E] tracking-tight">
                  Når misbrug og psykiske udfordringer hænger sammen
                </h3>
                <div className="space-y-4 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed">
                  <p>
                    Når psykisk sygdom og misbrug optræder samtidig, kan problemerne påvirke og forstærke hinanden. Det kaldes ofte en{' '}
                    <Link
                      to="/dobbeltdiagnose"
                      className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                    >
                      dobbeltdiagnose
                    </Link>
                    . I de situationer er det vigtigt, at døgnbehandling og misbrugsbehandling bliver tilrettelagt med blik for den samlede problematik.
                  </p>
                  <p>
                    Midtgaarden tilbyder døgnbehandling ved dobbeltdiagnose og kan inddrage både diagnosticerede og mistænkte psykiatriske problemstillinger. Forløbet kan også tage højde for andre samtidige udfordringer som ludomani, kriminalitet og adfærdsmæssige problemstillinger.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-[#1C1C1E]/10 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-[#F8FAFC] text-xs font-semibold rounded-none border border-[#1C1C1E]/10 text-[#1C1C1E]">
                  Dobbeltdiagnose
                </span>
                <span className="px-3 py-1 bg-[#F8FAFC] text-xs font-semibold rounded-none border border-[#1C1C1E]/10 text-[#1C1C1E]">
                  Psykiatrisk supervision
                </span>
                <span className="px-3 py-1 bg-[#F8FAFC] text-xs font-semibold rounded-none border border-[#1C1C1E]/10 text-[#1C1C1E]">
                  Ludomani & adfærd
                </span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 6: DØGNBEHANDLING I SEJSTRUP FOR VOKSNE FRA HELE LANDET */}
      <section className="py-16 sm:py-24 bg-white border-t border-[#1C1C1E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block mb-2">
                  GEOGRAFI & ERFARING
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  Døgnbehandling i Sejstrup for voksne fra hele landet
                </h2>
                <div className="w-16 h-1 bg-[#3885B8] my-4" />
              </div>
              <div className="space-y-4 text-base sm:text-lg text-[#1C1C1E]/80 leading-relaxed pt-1">
                <p>
                  Midtgaarden ligger i Sejstrup nær Esbjerg og modtager voksne fra hele landet. På vores center for{' '}
                  <Link
                    to="/"
                    className="font-medium text-[#3885B8] underline decoration-[#3885B8]/40 hover:decoration-[#3885B8] hover:text-[#2A6E9B] transition-colors"
                  >
                    misbrugsbehandling
                  </Link>{' '}
                  har vi mere end 20 års erfaring med døgnbehandling, alkoholbehandling og komplekse misbrugsforløb. Når det er relevant, kan halvvejshuset i Esbjerg indgå som en del af udslusningen.
                </p>
                <p>
                  Er du i tvivl om, hvorvidt døgnbehandling passer til din situation, er du velkommen til at kontakte Midtgaarden. Vi tager udgangspunkt i dine behov og giver dig et tydeligt billede af mulighederne. Det første skridt er en faglig og respektfuld samtale om vejen videre.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap gap-4">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#3885B8] text-white text-sm sm:text-base font-bold hover:bg-[#2A6E9B] transition-colors shadow-sm"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring {COMPANY_INFO.phone}</span>
                </a>
                <a
                  href={COMPANY_INFO.emailMailto}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-[#3885B8] text-[#3885B8] hover:bg-[#3885B8] hover:text-white text-sm sm:text-base font-semibold transition-colors"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>Send en besked</span>
                </a>
              </div>
            </div>

            {/* Visual showcase grid */}
            <div className="lg:col-span-6 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-md bg-white">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/samtale-i-laenestole-i-lyst-samtalerum.jpg"
                    alt="Mand og kvinde i rolig samtale i lænestole"
                    className="w-full h-48 sm:h-56 object-cover rounded-none"
                  />
                </div>
                <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-md bg-white">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/hvidmalet-bygning-med-graesplaene-og-hegn.jpg"
                    alt="Hvidmalet bygning med græsplæne og hegn"
                    className="w-full h-40 sm:h-44 object-cover rounded-none"
                  />
                </div>
              </div>

              <div className="space-y-4 pt-6">
                <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-md bg-white">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/faellesskab-og-samtale-omkring-kaffebord.jpg"
                    alt="Personer i munter samtale omkring et kaffebord"
                    className="w-full h-40 sm:h-44 object-cover rounded-none"
                  />
                </div>
                <div className="rounded-none overflow-hidden border border-[#1C1C1E]/10 shadow-md bg-white">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/faellesspisning-med-buffet-og-tallerkener.jpg"
                    alt="Fællesspisning med buffet anrettet ved et langt spisebord"
                    className="w-full h-48 sm:h-56 object-cover rounded-none"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 7: HVAD KOSTER DØGNBEHANDLING? */}
      <section className="py-16 sm:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-none border border-[#1C1C1E]/10 p-8 sm:p-12 shadow-xs">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              
              <div className="lg:col-span-8 space-y-4">
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3885B8] block">
                  ØKONOMI & VISITATION
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C1C1E] tracking-tight mb-4">
                  Hvad koster døgnbehandling?
                </h2>
                <div className="w-16 h-1 bg-[#3885B8] my-4" />
                <div className="space-y-3 text-sm sm:text-base text-[#1C1C1E]/80 leading-relaxed pt-1">
                  <p>
                    Prisen på døgnbehandling afhænger af forløbets indhold, varighed og den enkeltes støttebehov. Den konkrete pris afklares derfor i forbindelse med henvendelsen. Hvis en kommune eller myndighed er involveret, samarbejder vi om rammerne, handlingsplanen og den videre indsats.
                  </p>
                  <p>
                    Før et ophold drøfter vi den aktuelle situation og behovet for behandling. Det giver et mere præcist grundlag for at vurdere, om døgnbehandling er den rette løsning, og hvordan forløbet kan sammensættes. Du er velkommen til at kontakte os, hvis du ønsker en indledende afklaring.
                  </p>
                </div>
              </div>

              <div className="lg:col-span-4 bg-[#F8FAFC] rounded-none border border-[#1C1C1E]/10 p-6 sm:p-7 shadow-xs space-y-4 text-center lg:text-left">
                <div className="w-10 h-10 rounded-none bg-[#3885B8]/10 text-[#3885B8] flex items-center justify-center text-lg mx-auto lg:mx-0">
                  <i className="fa-solid fa-file-invoice-dollar" aria-hidden="true" />
                </div>
                <h4 className="text-base font-bold text-[#1C1C1E]">
                  Indledende afklaring og takster
                </h4>
                <p className="text-xs sm:text-sm text-[#1C1C1E]/75 leading-relaxed">
                  Kontakt os direkte for uforpligtende vejledning om visitationsprocedurer, takster og handleplaner.
                </p>
                <div className="pt-2 flex flex-col gap-2.5">
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#3885B8] text-white text-xs sm:text-sm font-bold hover:bg-[#2A6E9B] transition-colors"
                  >
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                    Ring {COMPANY_INFO.phone}
                  </a>
                  <Link
                    to="/priser"
                    className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-full border border-[#1C1C1E]/20 hover:border-[#1C1C1E] text-[#1C1C1E] text-xs sm:text-sm font-semibold transition-colors"
                  >
                    Læs mere under Priser
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* FÅ EN AFKLARENDE SAMTALE OM BEHANDLING / KONTAKT WEBFORM SECTION */}
      <section className="relative py-20 sm:py-28 bg-[#1C1C1E] text-white overflow-hidden">
        {/* Full-bleed background image with dark overlay */}
        <img
          src="https://assets.auxodev.dk/assets/caa9ce5a-9b51-4c31-bd1f-3a73d776d9c2/loungeomraade-med-laenestole-under-skraavaegge.jpg"
          alt="Loungeområde med sorte lænestole og sofabord under skråvægge med synlige bjælker"
          className="absolute inset-0 w-full h-full object-cover opacity-60 rounded-none"
        />
        <div className="absolute inset-0 bg-[#1C1C1E]/60 backdrop-blur-xs" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Column: Heading, text, buttons */}
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#3885B8]">
                AFKLARENDE SAMTALE & KONTAKT
              </span>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4">
                Få en afklarende samtale om behandling
              </h2>

              <p className="text-base sm:text-lg text-white/90 leading-relaxed font-normal">
                Har du brug for at få hjælp til at komme ud af misbrug, eller er du i kontakt med en, som har det? Så er du meget velkommen til at ringe hertil på telefon{' '}
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="text-white font-medium underline underline-offset-2 hover:text-[#3885B8] transition-colors whitespace-nowrap"
                >
                  {COMPANY_INFO.phone}
                </a>{' '}
                eller sende en e-mail til{' '}
                <a
                  href={COMPANY_INFO.emailMailto}
                  className="text-white font-medium underline underline-offset-2 hover:text-[#3885B8] transition-colors whitespace-nowrap"
                >
                  {COMPANY_INFO.email}
                </a>{' '}
                og høre mere om døgnbehandling og Midtgaarden generelt.
              </p>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] text-white font-semibold text-sm transition-all shadow-md"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring: {COMPANY_INFO.phone}</span>
                </a>

                <a
                  href={COMPANY_INFO.emailMailto}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white/90 font-semibold text-sm hover:border-white hover:text-white transition-all backdrop-blur-xs"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>{COMPANY_INFO.email}</span>
                </a>
              </div>
            </div>

            {/* Right Column: Webform structured vertically */}
            <div className="lg:col-span-6 bg-white/5 p-8 rounded-none border border-white/15 backdrop-blur-md shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-6">Send en besked</h3>

              {formSubmitted ? (
                <div className="p-6 bg-[#3885B8]/20 border border-[#3885B8] rounded-none text-center space-y-3">
                  <i className="fa-solid fa-circle-check text-3xl text-[#3885B8]" aria-hidden="true" />
                  <h4 className="text-lg font-bold text-white">Mange tak for din henvendelse</h4>
                  <p className="text-xs text-white/80">Vi har modtaget din besked og vender tilbage hurtigst muligt.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  
                  {/* Honeypot Antispam Field */}
                  <div className="sr-only" aria-hidden="true">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
                  </div>

                  {/* Top row 50/50 grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                        Navn *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="Dit navn"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        placeholder="Dine 8 cifre"
                        className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email field */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                      E-mailadresse *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="din@email.dk"
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Description / Message field */}
                  <div>
                    <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-1">
                      Beskrivelse *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      required
                      placeholder="Skriv din besked eller spørgsmål her..."
                      className="w-full bg-transparent border-b border-white/30 focus:border-white py-2 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors resize-none"
                    ></textarea>
                  </div>

                  {formError && (
                    <p className="text-xs font-semibold text-rose-400">{formError}</p>
                  )}

                  {/* Full-width pill-shaped primary CTA button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 px-6 rounded-full bg-[#3885B8] hover:bg-[#2A6E9B] text-white font-bold text-sm tracking-wider uppercase transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3885B8] disabled:opacity-50 cursor-pointer"
                  >
                    {submitting ? 'Sender henvendelse...' : 'Send henvendelse'}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
